/**
 * What a question looks like between the file and the bank.
 *
 * The old importer had no shape for this at all: a read produced a count, and
 * the review screen invented fifteen questions to show against it. So the
 * reviewer was asked to read every item with equal care, because nothing on
 * the screen could say which ones the machine was unsure about.
 *
 * Three fields carry that weight here:
 *
 * - **`confidence`** ranks the reader's attention. It is `null` for a parsed
 *   spreadsheet on purpose: a cell that says `B` is a fact, not a guess, and
 *   dressing it as 100% certain would put a made-up number next to a real one.
 * - **`page` and `sourceText`** let the reviewer check a suspicious extraction
 *   without leaving the app and opening the original file.
 * - **`duplicate`** is a scored match against the bank, so "we already have
 *   this" is a claim the reviewer can weigh rather than a banner they trust.
 *
 * Issues are **derived, never stored** (`importIssues`). A missing answer key
 * is a fact about the draft as it stands right now, so fixing the draft has to
 * clear the flag on the next render. Storing them would mean a reviewer could
 * repair an item and still be blocked by a stale error.
 */

import type { AuthoringReference } from "@/lib/library-authoring"
import {
  authoredChoicesForStem,
  authoredFillBlankForStem,
  correctIndexForChoices,
} from "@/lib/question-answer-key"

/** How the questions got here. Drives whether confidence means anything. */
export type ImportSourceKind = "document" | "table"

/** Where the reviewer has landed on one item. */
export type ImportItemDecision = "pending" | "accepted" | "rejected"

export type ImportIssueSeverity = "error" | "warning"

/**
 * `error` blocks the item from importing; `warning` is worth a look and
 * nothing more. Keeping the two apart is what makes partial commit possible:
 * without it, one malformed row in a file of two hundred holds up the batch.
 */
export interface ImportIssue {
  field: ImportItemField
  severity: ImportIssueSeverity
  message: string
}

export type ImportItemField =
  | "stem"
  | "type"
  | "options"
  | "answer"
  | "rationale"
  | "points"

/** An existing bank question this one looks like. */
export interface ImportDuplicateMatch {
  questionId: string
  title: string
  stem: string
  /** 0 to 1. Shown as a percentage, so the reviewer can discount a weak match. */
  score: number
  folderName: string
  timesUsed: number
  /** Answer choices when the bank row carries them. */
  options?: string[]
  /** Indexes into `options` for the keyed answer. */
  answerIndexes?: number[]
}

/** How the reviewer settled a duplicate. */
export type ImportDuplicateResolution = "unresolved" | "keep-new" | "keep-both"

export const IMPORT_QUESTION_TYPES = [
  "Multiple Choice",
  "Multiple Select",
  "True / False",
  "Fill in the Blank",
  "Short Answer",
  "Essay",
  "Matching",
] as const

export type ImportQuestionType = (typeof IMPORT_QUESTION_TYPES)[number]

/** Types where an unanswered item is not importable. */
const KEYED_TYPES = new Set<string>([
  "Multiple Choice",
  "Multiple Select",
  "True / False",
  "Fill in the Blank",
])

/** Types that need a list of choices to be worth anything. */
const OPTION_TYPES = new Set<string>([
  "Multiple Choice",
  "Multiple Select",
  "True / False",
])

export interface ImportedMatchingPair {
  id: string
  left: string
  right: string
}

export interface ImportedOrderedItem {
  id: string
  text: string
}

export interface ImportedHotspotRegion {
  id: string
  shape: "rect" | "circle"
  label: string
  isCorrect: boolean
  x: number
  y: number
  w: number
  h: number
}

export interface ImportedQuestionDraft {
  /** Short bank name — same job as the write-new Title field. */
  title: string
  stem: string
  type: string
  options: string[]
  /** Indexes into `options`. Empty means the read found no key. */
  answerIndexes: number[]
  rationale: string
  difficulty: "Easy" | "Medium" | "Hard" | ""
  bloom: string
  /** Cognitive level — same inspector field as Compose new question. */
  cogLevel: string
  points: number
  topic: string
  /** Exam category ids from Classification. Empty until the author picks one. */
  categoryIds?: string[]
  tags: string[]
  /** Library folder id when filing; empty until the author picks one. */
  folderId: string
  randomizeOptions: boolean
  multiSelectScoring: import("@/lib/library-authoring").AuthoringMultiSelectScoring
  unlimitedSelections: boolean
  /** @deprecated Prefer multiSelectScoring. Kept for older import fixtures. */
  partialCredit: boolean
  caseSensitive: boolean
  negativeMarking: boolean
  showFeedbackWhenReviewing: boolean
  eligibleForRandomDraw: boolean
  shuffleMatchingPairs: boolean
  shuffleOrderingItems: boolean
  /** Composer-only fields the review pane can edit before commit. */
  references?: AuthoringReference[]
  numericValue?: string
  numericTolerance?: string
  numericUnits?: string
  pairs?: ImportedMatchingPair[]
  orderedItems?: ImportedOrderedItem[]
  hotspotImageName?: string
  hotspotImageUrl?: string
  hotspotRegions?: ImportedHotspotRegion[]
}

export interface ExtractedQuestion {
  id: string
  /** Position in the source. Row number for a spreadsheet, order for a read. */
  ordinal: number
  /** 0 to 1, or `null` when the source was structured and nothing was guessed. */
  confidence: number | null
  /** Page it came off, when the source had pages. */
  page: number | null
  /** The original text, so the reviewer can check the extraction in place. */
  sourceText: string
  duplicate: ImportDuplicateMatch | null
  duplicateResolution: ImportDuplicateResolution
  decision: ImportItemDecision
  /**
   * When this one was actually taken into the bank, or `null`.
   *
   * A commit can be partial, so the batch outlives it: the questions that went
   * in are stamped and the rest stay reviewable. Without the stamp a second
   * commit on the same batch would import the first set twice.
   */
  importedAt: number | null
  /**
   * Reviewer applied a suggested fix (Leo, variations, or a field repair).
   * Clears the attention queue once the draft is no longer blocked, so Import
   * and the clean-items action can take it without waiting on the original
   * confidence band.
   */
  reviewResolved?: boolean
  draft: ImportedQuestionDraft
}

export type ImportConfidenceBand = "high" | "medium" | "low"

/**
 * Three bands, not a raw percentage in the list. A reviewer scanning two
 * hundred rows is deciding where to look, and "0.83" makes them do arithmetic
 * to answer a question the band already answers.
 */
export function confidenceBand(
  confidence: number | null
): ImportConfidenceBand | null {
  if (confidence === null) return null
  if (confidence >= 0.9) return "high"
  if (confidence >= 0.7) return "medium"
  return "low"
}

export const CONFIDENCE_BAND_LABEL: Record<ImportConfidenceBand, string> = {
  high: "High confidence",
  medium: "Worth a look",
  low: "Low confidence",
}

/** Shared list + header chrome for confidence bands (same tint / icon everywhere). */
export const CONFIDENCE_BAND_ICON: Record<ImportConfidenceBand, string> = {
  high: "fa-circle-check",
  medium: "fa-eye",
  low: "fa-circle-question",
}

export function confidenceBandTint(
  band: ImportConfidenceBand
): "success" | "warning" | "danger" {
  if (band === "high") return "success"
  if (band === "medium") return "warning"
  return "danger"
}

export function confidenceBandReason(band: ImportConfidenceBand): string {
  if (band === "high") {
    return "The extraction looks solid. Still worth a quick skim before you add it."
  }
  if (band === "medium") {
    return "This item may be too discriminating or too easy for the cohort, or the stem could read more clearly. Skim it, or create variations with Leo."
  }
  return "The read was unsure about this one. Check the stem and answer key before you add it, or create variations with Leo."
}

/**
 * Everything wrong with an item as it currently stands.
 *
 * Ordered by severity so a caller taking the first issue gets the one that
 * blocks, not the one that merely nags.
 */
export function importIssues(item: ExtractedQuestion): ImportIssue[] {
  const { draft } = item
  const issues: ImportIssue[] = []

  if (!draft.stem.trim()) {
    issues.push({
      field: "stem",
      severity: "error",
      message: "No question text was found.",
    })
  }

  if (!draft.type.trim()) {
    issues.push({
      field: "type",
      severity: "error",
      message: "No question type was found.",
    })
  }

  const options = resolvedImportOptions(draft)

  if (OPTION_TYPES.has(draft.type)) {
    if (options.length < 2) {
      issues.push({
        field: "options",
        severity: "error",
        message: "Needs at least two answer choices.",
      })
    } else if (options.length < draft.options.length) {
      issues.push({
        field: "options",
        severity: "warning",
        message: "One or more choices came through blank.",
      })
    }
  }

  if (KEYED_TYPES.has(draft.type)) {
    if (draft.type === "Fill in the Blank") {
      if (options.length === 0 && !authoredFillBlankForStem(draft.stem)) {
        issues.push({
          field: "answer",
          severity: "error",
          message: "No accepted answer was found for the blank.",
        })
      }
    } else if (resolvedAnswerIndexes(draft).length === 0) {
      issues.push({
        field: "answer",
        severity: "error",
        message: "No correct answer was marked.",
      })
    } else if (
      draft.type === "Multiple Choice" &&
      resolvedAnswerIndexes(draft).length > 1
    ) {
      issues.push({
        field: "answer",
        severity: "error",
        message: "Multiple choice takes one correct answer.",
      })
    }
  }

  if (draft.type === "Matching") {
    const pairs = (draft.pairs ?? []).filter(
      (pair) => pair.left.trim() && pair.right.trim(),
    )
    if (pairs.length < 2) {
      issues.push({
        field: "answer",
        severity: "error",
        message: "Needs at least two matching pairs.",
      })
    }
  }

  if (draft.points <= 0) {
    issues.push({
      field: "points",
      severity: "warning",
      message: "Points default to 1 on import.",
    })
  }

  if (!draft.rationale.trim()) {
    issues.push({
      field: "rationale",
      severity: "warning",
      message: "No rationale was found.",
    })
  }

  return issues.sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1
  )
}

/** An item with a blocking issue cannot be accepted, and says which field. */
export function blockingIssues(item: ExtractedQuestion): ImportIssue[] {
  return importIssues(item).filter((issue) => issue.severity === "error")
}

export function isBlocked(item: ExtractedQuestion): boolean {
  return blockingIssues(item).length > 0
}

/**
 * Whether this item is one of the ones to actually read.
 *
 * Anything blocked, weakly extracted, or matching something already in the
 * bank. Clean high-confidence items are the ones the reviewer should be able
 * to wave through in a single bulk action, which is the whole point of
 * carrying confidence in the first place.
 */
export function needsAttention(item: ExtractedQuestion): boolean {
  if (item.importedAt !== null) return false
  // Settled items leave the attention queue — taking into the pool clears it.
  if (item.decision === "accepted" || item.decision === "rejected") return false
  if (isBlocked(item)) return true
  if (item.duplicate && item.duplicateResolution === "unresolved") return true
  // A suggested fix the reviewer accepted is enough to wave it through.
  if (item.reviewResolved) return false
  const band = confidenceBand(item.confidence)
  return band === "low" || band === "medium"
}

/** Clean enough to add without reading. Drives the bulk wave-through. */
export function isWaveThrough(item: ExtractedQuestion): boolean {
  return item.decision === "pending" && !needsAttention(item)
}

/**
 * Can go into the destination now — no separate Accept step.
 *
 * Pending or already accepted, unblocked, not already taken, and not a
 * duplicate left hanging. Rejected items stay out.
 */
export function willImport(item: ExtractedQuestion): boolean {
  if (item.importedAt !== null) return false
  if (item.decision === "rejected") return false
  if (isBlocked(item)) return false
  if (item.duplicate && item.duplicateResolution === "unresolved") return false
  return true
}

/**
 * Ready for the batch Import control.
 *
 * Same as {@link willImport} except an unresolved duplicate is included.
 * Taking it keeps both copies, so Duplicate is not a reason to leave the
 * question on the review page.
 */
export function canTakeNow(item: ExtractedQuestion): boolean {
  if (item.importedAt !== null) return false
  if (item.decision === "rejected") return false
  if (isBlocked(item)) return false
  return true
}

/**
 * What the read turned up, scored as at extraction time.
 *
 * After Import as draft stamps `importedAt` / resolves duplicates, live
 * `importCounts` would zero out Needs attention and Duplicates. The job
 * panel's "What came out" tiles still need the extraction snapshot.
 */
export function importExtractionCounts(
  items: readonly ExtractedQuestion[]
): ImportCounts {
  return importCounts(
    items.map((item) => ({
      ...item,
      importedAt: null,
      decision: item.decision === "rejected" ? "rejected" : "pending",
      duplicateResolution: item.duplicate
        ? "unresolved"
        : item.duplicateResolution,
    }))
  )
}

export interface ImportCounts {
  total: number
  willImport: number
  /** Already taken into the bank by an earlier commit on this same batch. */
  imported: number
  accepted: number
  rejected: number
  pending: number
  blocked: number
  duplicates: number
  needsAttention: number
  waveThrough: number
}

export function importCounts(items: ExtractedQuestion[]): ImportCounts {
  const counts: ImportCounts = {
    total: items.length,
    willImport: 0,
    imported: 0,
    accepted: 0,
    rejected: 0,
    pending: 0,
    blocked: 0,
    duplicates: 0,
    needsAttention: 0,
    waveThrough: 0,
  }

  for (const item of items) {
    if (item.importedAt !== null) counts.imported += 1
    if (item.decision === "accepted") counts.accepted += 1
    if (item.decision === "rejected") counts.rejected += 1
    if (item.decision === "pending") counts.pending += 1
    // Rejected and already-imported items are settled, so they stop counting
    // against the reviewer even when one is still missing a stem or a key.
    const settled = item.decision === "rejected" || item.importedAt !== null
    if (!settled && isBlocked(item)) counts.blocked += 1
    if (!settled && item.duplicate) counts.duplicates += 1
    if (!settled && needsAttention(item)) counts.needsAttention += 1
    if (isWaveThrough(item)) counts.waveThrough += 1
    if (willImport(item)) counts.willImport += 1
  }

  return counts
}

/** The tabs over the triage list. `all` last so the flagged views lead. */
export type ImportTriageFilter =
  | "attention"
  | "duplicates"
  | "imported"
  | "all"

export function matchesTriageFilter(
  item: ExtractedQuestion,
  filter: ImportTriageFilter
): boolean {
  switch (filter) {
    case "attention":
      return needsAttention(item)
    case "duplicates":
      return (
        item.decision === "pending" &&
        item.duplicate !== null &&
        item.duplicateResolution === "unresolved"
      )
    case "imported":
      return item.importedAt !== null
    case "all":
      return true
  }
}

export function triageFilterCount(
  items: readonly ExtractedQuestion[],
  filter: ImportTriageFilter
): number {
  return items.reduce(
    (total, item) => (matchesTriageFilter(item, filter) ? total + 1 : total),
    0
  )
}

/**
 * Rows the list-header checkbox should cover.
 *
 * Select all covers every visible row, including Needs attention. Import all
 * is the clean wave-through and stays on {@link isWaveThrough}.
 */
export function selectableTriageItems(
  items: readonly ExtractedQuestion[],
  filter: ImportTriageFilter
): ExtractedQuestion[] {
  return items.filter((item) => matchesTriageFilter(item, filter))
}

/**
 * Where to land the reviewer when the route opens.
 *
 * Always All so the batch is visible at once. Flagged rows still carry
 * badges in the list; the reviewer is not dropped onto a subset.
 */
export function defaultTriageFilter(
  _items: ExtractedQuestion[]
): ImportTriageFilter {
  return "all"
}

function resolvedImportOptions(draft: ImportedQuestionDraft): string[] {
  const options = draft.options.filter((option) => option.trim())
  if (options.length > 0) return options
  return authoredChoicesForStem(draft.stem)?.map((choice) => choice.text) ?? []
}

function resolvedAnswerIndexes(draft: ImportedQuestionDraft): number[] {
  const options = resolvedImportOptions(draft)
  const authored = authoredChoicesForStem(draft.stem)
  if (authored) {
    const indexes = options.flatMap((option, index) => {
      const match = authored.find(
        (choice) => choice.text.trim().toLowerCase() === option.trim().toLowerCase(),
      )
      return match?.correct ? [index] : []
    })
    if (indexes.length > 0) return indexes
  }
  if (draft.answerIndexes.length > 0) return draft.answerIndexes
  const keyed = correctIndexForChoices(draft.stem, options)
  return keyed != null ? [keyed] : []
}

/** Short label for the answer key, for list rows and compare views. */
export function answerLabel(draft: ImportedQuestionDraft): string {
  const indexes = resolvedAnswerIndexes(draft)
  if (indexes.length === 0) return "None"
  return indexes
    .slice()
    .sort((a, b) => a - b)
    .map((index) => String.fromCharCode(65 + index))
    .join(", ")
}

/**
 * "1 question" / "4 questions".
 *
 * Counts land in button labels and banner titles all over this flow, and a
 * button that reads "Import 1 questions" is the kind of thing that makes an
 * author distrust the number next to it.
 */
export function questionCount(count: number): string {
  return `${count} ${count === 1 ? "question" : "questions"}`
}

/** First line of a stem, clipped, for a row that has one line to spend. */
export function stemPreview(stem: string, max = 120): string {
  const flat = stem.replace(/\s+/g, " ").trim()
  if (!flat) return "No question text"
  return flat.length > max ? `${flat.slice(0, max - 1)}\u2026` : flat
}

export const IMPORT_MAX_BYTES = 10 * 1024 * 1024

export const IMPORT_DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx"] as const
export const IMPORT_TABLE_EXTENSIONS = ["csv"] as const

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".")
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase()
}

export function formatImportBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/**
 * Why a picked file cannot start a run, in the author's terms.
 *
 * Returns `null` when the file is fine. Checked before anything is written to
 * the store, so a rejected file never becomes a record the author has to go
 * and clean up.
 */
export function rejectFileReason(
  file: { name: string; size: number },
  kind: ImportSourceKind
): string | null {
  const allowed =
    kind === "document" ? IMPORT_DOCUMENT_EXTENSIONS : IMPORT_TABLE_EXTENSIONS
  const extension = fileExtension(file.name)

  if (!extension) {
    return "That file has no extension, so there is no way to tell what is in it."
  }

  if (!(allowed as readonly string[]).includes(extension)) {
    const list = allowed.map((entry) => entry.toUpperCase()).join(", ")
    return `${extension.toUpperCase()} files are not read here. Pick ${list}, or switch the source above.`
  }

  if (file.size === 0) {
    return "That file is empty."
  }

  if (file.size > IMPORT_MAX_BYTES) {
    return `That file is ${formatImportBytes(file.size)}. The limit is ${formatImportBytes(IMPORT_MAX_BYTES)}.`
  }

  return null
}
